/**
 * TagSelectRow View Component
 *
 * Renders a tag row in select mode with checkbox for toggling selection.
 * Shows the tag color, name, and player usage count.
 * @module features/player-kanban/components/TagSelectRow
 */

import { Check } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/*
 * design-system: dynamic-style waiver — data-driven tag colour swatch (no
 * semantic token); passed as a single style reference so no inline object
 * literal renders on a JSX element.
 */
const swatchStyle = (color: string) => ({ backgroundColor: color });

/**
 * Props for TagSelectRowView component
 */
interface TagSelectRowViewProps {
  readonly tagName: string;
  readonly tagColor: string;
  readonly isSelected: boolean;
  readonly playerCount: number;
  readonly onPress: () => void;
}

/**
 * Tag row display in select mode
 * @param root0 - Component props
 * @param root0.tagName - The name of the tag
 * @param root0.tagColor - The color of the tag
 * @param root0.isSelected - Whether the tag is currently selected
 * @param root0.playerCount - Number of players with this tag
 * @param root0.onPress - Callback when row is pressed
 */
const TagSelectRowView = ({
  tagName,
  tagColor,
  isSelected,
  playerCount,
  onPress,
}: TagSelectRowViewProps) => (
  <Pressable
    onPress={onPress}
    className="p-3"
    accessible
    accessibilityLabel={`${isSelected ? "Deselect" : "Select"} ${tagName} tag`}
    accessibilityRole="checkbox"
    accessibilityState={{ checked: isSelected }}
  >
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack direction="horizontal" className="flex-1 items-center gap-3">
        <Box
          className={`size-5 items-center justify-center rounded-none border ${
            isSelected
              ? "border-accent-primary bg-accent-primary"
              : "border-content-subtle bg-transparent"
          }`}
        >
          {/* The tick sits on the filled accent box, so it needs the on-accent
              colour. */}
          {isSelected && (
            <Icon as={Check} size="xs" className="text-content-on-accent" />
          )}
        </Box>
        {/* design-system: dynamic-style waiver — data-driven value (tag color) */}
        <Box
          className="size-4 rounded-full"
          // eslint-disable-next-line no-restricted-syntax -- data-driven tag swatch color; no semantic token
          UNSAFE_style={swatchStyle(tagColor)}
        />
        <Text variant="title-sm-medium" className="flex-1 text-content-primary">
          {tagName}
        </Text>
        <Text variant="caption" className="text-content-subtle">
          {playerCount} players
        </Text>
      </Stack>
    </Stack>
  </Pressable>
);

TagSelectRowView.displayName = "TagSelectRowView";

export default memo(TagSelectRowView);

export type { TagSelectRowViewProps };
